/**
 * CSRM Page — Client Success & Relationship Management
 * ─────────────────────────────────────────────────────
 * Single tabbed page covering Phase 1–4:
 *   • My Today          — auto-generated tasks + open queries for the logged-in user
 *   • Comm Plans        — configurable schedules per client × activity
 *   • Queries           — universal query tracker (WA/Email/Phone/Meeting/Walk-in)
 *   • Meetings          — meeting records + MoM
 *   • Action Items      — cross-meeting action-item board
 *   • Health & Manager  — client health scores + manager dashboard
 *   • AI Assist         — MoM summariser, action-item extractor, at-risk client digest
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SearchableSelect from '../components/SearchableSelect';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  done: 'bg-emerald-100 text-emerald-800',
  skipped: 'bg-gray-100 text-gray-600',
  open: 'bg-amber-100 text-amber-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  escalated: 'bg-rose-100 text-rose-800',
  cancelled: 'bg-gray-100 text-gray-600',
  submitted: 'bg-emerald-100 text-emerald-800',
};

const clientName = (c) => (c.company_name || `${c.first_name || ''} ${c.last_name || ''}`).trim() || '(unnamed client)';

export default function CSRMPage() {
  const [tab, setTab] = useState('worksheet');
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/clients`, opts).then((r) => setClients(r.data || [])).catch(() => {});
    axios.get(`${API}/api/users`, opts).then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const clientLabel = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, clientName(c)])), [clients]);
  const userLabel = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name || u.email])), [users]);

  const TABS = [
    { k: 'worksheet', label: 'Worksheet' },
    { k: 'calendar', label: 'Calendar' },
    { k: 'today', label: 'My Today' },
    { k: 'plans', label: 'Comm Plans' },
    { k: 'queries', label: 'Queries' },
    { k: 'meetings', label: 'Meetings' },
    { k: 'actions', label: 'Action Items' },
    { k: 'health', label: 'Health & Manager' },
    { k: 'ai', label: 'AI Assist' },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="csrm-page">
      <div>
        <h1 className="text-2xl font-bold text-emerald-800">Client Success (CSRM)</h1>
        <p className="text-sm text-gray-600">Every client, every commitment — tracked, measured, delivered.</p>
      </div>

      <div className="flex gap-1 flex-wrap border-b">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${tab === t.k ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid={`csrm-tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'worksheet' && <WorksheetTab clients={clients} users={users} clientLabel={clientLabel} userLabel={userLabel} />}
      {tab === 'calendar' && <CalendarTab clients={clients} users={users} clientLabel={clientLabel} userLabel={userLabel} />}
      {tab === 'today' && <MyTodayTab clientLabel={clientLabel} />}
      {tab === 'plans' && <PlansTab clients={clients} users={users} clientLabel={clientLabel} userLabel={userLabel} />}
      {tab === 'queries' && <QueriesTab clients={clients} users={users} clientLabel={clientLabel} userLabel={userLabel} />}
      {tab === 'meetings' && <MeetingsTab clients={clients} clientLabel={clientLabel} />}
      {tab === 'actions' && <ActionItemsTab clients={clients} clientLabel={clientLabel} userLabel={userLabel} />}
      {tab === 'health' && <HealthTab />}
      {tab === 'ai' && <AiTab clients={clients} clientLabel={clientLabel} />}
    </div>
  );
}

// ───────────────────────── Worksheet (Excel-like grid) ─────────────────────────
/**
 * Inline-editable grid of CSRM comm tasks. Every row is a task with editable
 * cells: Client, Activity, Category, Assigned, Due, Priority, Status. Changes
 * are pushed to the backend on cell blur / select-change. Users can add new
 * rows at the bottom or via the "+ Add Row" button. Marking a task done via
 * the checkbox is a single click.
 */
const CATEGORIES = ['SEO', 'GBP', 'GA', 'Ads', 'Social', 'general'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const STATUSES = ['pending', 'in_progress', 'done', 'skipped'];

function WorksheetTab({ clients, users, clientLabel, userLabel }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterClient, setFilterClient] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('open');
  const [savingCell, setSavingCell] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterClient !== 'all') params.set('client_id', filterClient);
      if (filterAssignee !== 'all') params.set('assigned_to', filterAssignee);
      if (filterStatus !== 'all' && filterStatus !== 'open') params.set('status', filterStatus);
      const r = await axios.get(`${API}/api/csrm/tasks?${params.toString()}`, opts);
      let list = r.data || [];
      if (filterStatus === 'open') list = list.filter((t) => t.status !== 'done' && t.status !== 'skipped');
      setRows(list);
    } catch (e) { toast.error('Load failed: ' + (e.response?.data?.detail || e.message)); }
    setLoading(false);
  }, [filterClient, filterAssignee, filterStatus]);
  useEffect(() => { load(); }, [load]);

  const patchRow = async (id, patch) => {
    setSavingCell(id + Object.keys(patch)[0]);
    // Optimistic update
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, ...patch } : r));
    try {
      await axios.put(`${API}/api/csrm/tasks/${id}`, patch, opts);
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
      load();
    } finally { setSavingCell(''); }
  };

  const addBlankRow = async () => {
    const firstClient = clients.filter((c) => !!c.id)[0];
    if (!firstClient) return toast.error('Add a client first to create a worksheet row.');
    const today = new Date().toISOString().slice(0, 10);
    const draft = {
      client_id: firstClient.id,
      activity: '',
      category: 'general',
      due_date: today,
      priority: 'medium',
      status: 'pending',
      assigned_to: '',
    };
    try {
      const r = await axios.post(`${API}/api/csrm/tasks`, draft, opts);
      setRows((prev) => [r.data, ...prev]);
      toast.success('Row added — click the Activity cell to edit');
    } catch (e) { toast.error('Add failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Delete this row?')) return;
    try {
      await axios.delete(`${API}/api/csrm/tasks/${id}`, opts);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) { toast.error('Delete failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const toggleDone = (row) => {
    const next = row.status === 'done' ? 'pending' : 'done';
    patchRow(row.id, { status: next });
  };

  const rowClass = (r) => {
    if (r.status === 'done') return 'bg-emerald-50/40';
    if (r.status === 'skipped') return 'bg-gray-50 opacity-70';
    if (r.due_date && r.due_date < new Date().toISOString().slice(0, 10)) return 'bg-rose-50/50';
    return '';
  };

  const activeClients = clients.filter((c) => !!c.id);
  const activeUsers = users.filter((u) => !!u.id);

  return (
    <div className="space-y-3" data-testid="csrm-worksheet">
      {/* Toolbar */}
      <div className="flex gap-2 items-center flex-wrap bg-white p-3 rounded-lg border">
        <Button size="sm" onClick={addBlankRow} data-testid="worksheet-add-row" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          + Add Row
        </Button>
        <div className="h-6 w-px bg-gray-300" />
        <Label className="text-xs text-gray-600">Client:</Label>
        <div className="min-w-[180px]">
          <SearchableSelect
            testId="worksheet-filter-client"
            value={filterClient}
            onChange={setFilterClient}
            placeholder="All Clients"
            options={[
              { value: 'all', label: 'All Clients' },
              ...activeClients.map((c) => ({ value: c.id, label: clientName(c), search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''}` })),
            ]}
          />
        </div>
        <Label className="text-xs text-gray-600">Assignee:</Label>
        <div className="min-w-[160px]">
          <SearchableSelect
            testId="worksheet-filter-assignee"
            value={filterAssignee}
            onChange={setFilterAssignee}
            placeholder="Everyone"
            options={[
              { value: 'all', label: 'Everyone' },
              ...activeUsers.map((u) => ({ value: u.id, label: u.name || u.email, sublabel: u.email })),
            ]}
          />
        </div>
        <Label className="text-xs text-gray-600">Status:</Label>
        <select className="text-xs border rounded px-2 py-1 bg-white" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="worksheet-filter-status">
          <option value="open">Open (default)</option>
          <option value="all">All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="text-xs text-gray-500 ml-auto">{rows.length} row{rows.length !== 1 ? 's' : ''}{loading ? ' · loading…' : ''}</div>
      </div>

      {/* Grid */}
      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-slate-50 border-b sticky top-0">
            <tr className="text-xs uppercase tracking-wider text-slate-600">
              <th className="p-2 text-left w-10">✓</th>
              <th className="p-2 text-left">Client</th>
              <th className="p-2 text-left">Activity</th>
              <th className="p-2 text-left w-24">Category</th>
              <th className="p-2 text-left w-40">Assigned To</th>
              <th className="p-2 text-left w-32">Due Date</th>
              <th className="p-2 text-left w-24">Priority</th>
              <th className="p-2 text-left w-32">Status</th>
              <th className="p-2 text-left w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={9} className="text-center py-10 text-gray-400 italic">No tasks match this filter. Click <span className="text-emerald-700 font-semibold">+ Add Row</span> to create one.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className={`border-b hover:bg-slate-50/50 transition-colors ${rowClass(r)}`} data-testid={`worksheet-row-${r.id}`}>
                <td className="p-2">
                  <input
                    type="checkbox"
                    checked={r.status === 'done'}
                    onChange={() => toggleDone(r)}
                    className="w-4 h-4 accent-emerald-600 cursor-pointer"
                    data-testid={`worksheet-done-${r.id}`}
                    title={r.status === 'done' ? 'Mark not done' : 'Mark done'}
                  />
                </td>
                <td className="p-1">
                  <select className="w-full text-sm px-2 py-1.5 border-0 bg-transparent hover:bg-white hover:border rounded focus:bg-white focus:border focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={r.client_id || ''} onChange={(e) => patchRow(r.id, { client_id: e.target.value })}>
                    {activeClients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
                  </select>
                </td>
                <td className="p-1">
                  <input type="text" className="w-full text-sm px-2 py-1.5 border-0 bg-transparent hover:bg-white hover:border rounded focus:bg-white focus:border focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    defaultValue={r.activity || ''}
                    placeholder="Task description…"
                    onBlur={(e) => e.target.value !== (r.activity || '') && patchRow(r.id, { activity: e.target.value })}
                    onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                    data-testid={`worksheet-activity-${r.id}`} />
                </td>
                <td className="p-1">
                  <select className="w-full text-xs px-1 py-1.5 border-0 bg-transparent hover:bg-white hover:border rounded focus:bg-white focus:border focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={r.category || 'general'} onChange={(e) => patchRow(r.id, { category: e.target.value })}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td className="p-1">
                  <select className="w-full text-xs px-1 py-1.5 border-0 bg-transparent hover:bg-white hover:border rounded focus:bg-white focus:border focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={r.assigned_to || ''} onChange={(e) => patchRow(r.id, { assigned_to: e.target.value })}>
                    <option value="">— Unassigned —</option>
                    {activeUsers.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                  </select>
                </td>
                <td className="p-1">
                  <input type="date" className="w-full text-xs px-1 py-1.5 border-0 bg-transparent hover:bg-white hover:border rounded focus:bg-white focus:border focus:outline-none focus:ring-1 focus:ring-emerald-500"
                    value={r.due_date || ''} onChange={(e) => patchRow(r.id, { due_date: e.target.value })} />
                </td>
                <td className="p-1">
                  <select className={`w-full text-xs font-semibold px-1 py-1.5 border-0 bg-transparent hover:bg-white hover:border rounded focus:bg-white focus:border focus:outline-none focus:ring-1 focus:ring-emerald-500 ${r.priority === 'urgent' ? 'text-rose-700' : r.priority === 'high' ? 'text-amber-700' : ''}`}
                    value={r.priority || 'medium'} onChange={(e) => patchRow(r.id, { priority: e.target.value })}>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                  </select>
                </td>
                <td className="p-1">
                  <select className={`w-full text-xs font-semibold px-1 py-1.5 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500 border ${STATUS_COLORS[r.status] || 'bg-white'}`}
                    value={r.status || 'pending'} onChange={(e) => patchRow(r.id, { status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                  </select>
                </td>
                <td className="p-1">
                  <button onClick={() => deleteRow(r.id)} className="text-rose-500 hover:text-rose-700 text-lg leading-none p-1" title="Delete row" data-testid={`worksheet-del-${r.id}`}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-gray-500 italic">
        💡 Tip: Every change auto-saves. Click a cell to edit inline. Rows highlighted <span className="bg-rose-100 px-1 rounded">rose</span> are overdue,
        <span className="bg-emerald-100 px-1 rounded ml-1">green</span> are done. Assigned tasks automatically appear in the assignee&apos;s My Tasks &amp; Daily Standup.
      </div>
    </div>
  );
}

// ───────────────────────── Calendar (month-grid view of recurring CSRM tasks) ─────────────────────────
/**
 * Month calendar showing every scheduled CSRM comm task (from plans + ad-hoc) for the
 * selected month. Filter by client + assignee. Click a day → drill into its tasks below.
 *
 * Data source: /api/csrm/tasks?from_date&to_date (server-side). We proactively call
 * /api/csrm/tasks/generate?days_ahead=60 once on mount so recurring-plan tasks are
 * pre-materialised for the next two months and show up on the grid.
 */
const toLocalIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const CATEGORY_COLOR = {
  SEO: 'bg-blue-100 text-blue-800',
  GBP: 'bg-emerald-100 text-emerald-800',
  GA: 'bg-purple-100 text-purple-800',
  Ads: 'bg-amber-100 text-amber-800',
  Social: 'bg-pink-100 text-pink-800',
  general: 'bg-gray-100 text-gray-700',
};

function CalendarTab({ clients, users, clientLabel, userLabel }) {
  const [monthStart, setMonthStart] = useState(() => {
    const now = new Date();
    return toLocalIso(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [tasks, setTasks] = useState([]);
  const [filterClient, setFilterClient] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [activeDay, setActiveDay] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const monthLabel = useMemo(() => {
    const d = new Date(monthStart);
    return d.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
  }, [monthStart]);

  const { fromDate, toDate } = useMemo(() => {
    const base = new Date(monthStart);
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    return { fromDate: toLocalIso(first), toDate: toLocalIso(last) };
  }, [monthStart]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      if (filterClient !== 'all') params.set('client_id', filterClient);
      if (filterAssignee !== 'all') params.set('assigned_to', filterAssignee);
      const r = await axios.get(`${API}/api/csrm/tasks?${params.toString()}`, opts);
      setTasks(r.data || []);
    } catch (e) { toast.error('Load failed: ' + (e.response?.data?.detail || e.message)); }
    setLoading(false);
  }, [fromDate, toDate, filterClient, filterAssignee]);
  useEffect(() => { load(); }, [load]);

  // On first mount, generate up to 60 days of upcoming tasks from active plans so
  // the calendar isn't empty for future months. Idempotent per plan+date.
  useEffect(() => {
    (async () => {
      try {
        setGenerating(true);
        await axios.post(`${API}/api/csrm/tasks/generate?days_ahead=60`, {}, opts);
      } catch (_e) { /* silent — non-admin users get 403, that's fine */ }
      finally { setGenerating(false); load(); }
    })();
  }, []);

  const prevMonth = () => {
    const d = new Date(monthStart);
    setMonthStart(toLocalIso(new Date(d.getFullYear(), d.getMonth() - 1, 1)));
    setActiveDay('');
  };
  const nextMonth = () => {
    const d = new Date(monthStart);
    setMonthStart(toLocalIso(new Date(d.getFullYear(), d.getMonth() + 1, 1)));
    setActiveDay('');
  };
  const goToday = () => {
    const now = new Date();
    setMonthStart(toLocalIso(new Date(now.getFullYear(), now.getMonth(), 1)));
    setActiveDay(toLocalIso(now));
  };

  const tasksByDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => { const d = t.due_date; if (!d) return; (map[d] = map[d] || []).push(t); });
    return map;
  }, [tasks]);

  const dayCells = useMemo(() => {
    const base = new Date(monthStart);
    const first = new Date(base.getFullYear(), base.getMonth(), 1);
    const last = new Date(base.getFullYear(), base.getMonth() + 1, 0);
    const cells = [];
    for (let i = 0; i < first.getDay(); i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      cells.push(toLocalIso(new Date(base.getFullYear(), base.getMonth(), d)));
    }
    return cells;
  }, [monthStart]);

  const today = toLocalIso(new Date());
  const dayTasks = activeDay ? (tasksByDay[activeDay] || []) : [];

  const markDone = async (id) => {
    try {
      await axios.put(`${API}/api/csrm/tasks/${id}`, { status: 'done' }, opts);
      toast.success('Task marked done');
      load();
    } catch (e) { toast.error('Update failed: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-3" data-testid="csrm-calendar">
      {/* Toolbar */}
      <div className="flex gap-2 items-center flex-wrap bg-white p-3 rounded-lg border">
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={prevMonth} data-testid="cal-prev">←</Button>
          <div className="text-sm font-bold min-w-[160px] text-center" data-testid="cal-month-label">{monthLabel}</div>
          <Button size="sm" variant="outline" onClick={nextMonth} data-testid="cal-next">→</Button>
          <Button size="sm" variant="outline" onClick={goToday} data-testid="cal-today">Today</Button>
        </div>
        <div className="h-6 w-px bg-gray-300" />
        <Label className="text-xs text-gray-600">Client:</Label>
        <div className="min-w-[180px]">
          <SearchableSelect
            testId="cal-filter-client"
            value={filterClient}
            onChange={setFilterClient}
            placeholder="All Clients"
            options={[
              { value: 'all', label: 'All Clients' },
              ...clients.filter((c) => !!c.id).map((c) => ({ value: c.id, label: clientName(c), search: `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''}` })),
            ]}
          />
        </div>
        <Label className="text-xs text-gray-600">Assignee:</Label>
        <div className="min-w-[160px]">
          <SearchableSelect
            testId="cal-filter-assignee"
            value={filterAssignee}
            onChange={setFilterAssignee}
            placeholder="Everyone"
            options={[
              { value: 'all', label: 'Everyone' },
              ...users.filter((u) => !!u.id).map((u) => ({ value: u.id, label: u.name || u.email, sublabel: u.email })),
            ]}
          />
        </div>
        <div className="text-xs text-gray-500 ml-auto">
          {tasks.length} task{tasks.length !== 1 ? 's' : ''} in {monthLabel}
          {(loading || generating) && ' · loading…'}
        </div>
      </div>

      {/* Month grid */}
      <Card className="p-3">
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wider mb-1 text-gray-500">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {dayCells.map((iso, i) => {
            if (!iso) return <div key={i} />;
            const list = tasksByDay[iso] || [];
            const isToday = iso === today;
            const isActive = iso === activeDay;
            const pendingCount = list.filter((t) => t.status !== 'done' && t.status !== 'skipped').length;
            return (
              <button
                key={i}
                onClick={() => setActiveDay(iso === activeDay ? '' : iso)}
                className={`text-left rounded p-1.5 min-h-[92px] border transition-colors ${isActive ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' : isToday ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}
                data-testid={`cal-day-${iso}`}
              >
                <div className="flex items-center justify-between">
                  <div className={`text-[11px] font-bold ${isToday ? 'text-amber-700' : 'text-slate-700'}`}>{Number(iso.slice(-2))}</div>
                  {pendingCount > 0 && (
                    <span className="text-[9px] font-bold px-1 rounded bg-rose-500 text-white">{pendingCount}</span>
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {list.slice(0, 3).map((t) => (
                    <div
                      key={t.id}
                      className={`text-[9px] truncate rounded px-1 py-0.5 ${CATEGORY_COLOR[t.category] || CATEGORY_COLOR.general} ${t.status === 'done' ? 'line-through opacity-60' : ''}`}
                      title={`${t.activity} · ${clientLabel[t.client_id] || ''}`}
                    >
                      {t.activity}
                    </div>
                  ))}
                  {list.length > 3 && (
                    <div className="text-[9px] text-gray-500">+{list.length - 3} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Day details */}
      {activeDay && (
        <Card className="p-4" data-testid="cal-day-detail">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-emerald-800">
              {new Date(activeDay).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              <span className="text-xs font-normal text-gray-500 ml-2">({dayTasks.length} task{dayTasks.length !== 1 ? 's' : ''})</span>
            </h3>
            <Button size="sm" variant="outline" onClick={() => setActiveDay('')} data-testid="cal-day-close">Close</Button>
          </div>
          {dayTasks.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center">No CSRM commitments scheduled for this day.</div>
          ) : (
            <ul className="space-y-1.5">
              {dayTasks.map((t) => (
                <li key={t.id} className={`flex items-center gap-3 p-2 rounded border ${t.status === 'done' ? 'bg-emerald-50/60 border-emerald-200' : 'bg-white border-gray-200 hover:bg-slate-50'}`} data-testid={`cal-task-${t.id}`}>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${CATEGORY_COLOR[t.category] || CATEGORY_COLOR.general}`}>{t.category}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${t.status === 'done' ? 'line-through text-gray-500' : 'text-gray-900'}`}>{t.activity}</div>
                    <div className="text-[11px] text-gray-500">
                      🤝 {clientLabel[t.client_id] || t.client_id}
                      {t.assigned_to && <> · 👤 {userLabel[t.assigned_to] || 'assigned'}</>}
                      {t.priority && t.priority !== 'medium' && <> · <span className="uppercase font-bold">{t.priority}</span></>}
                    </div>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_COLORS[t.status] || ''}`}>{t.status}</span>
                  {t.status !== 'done' && (
                    <Button size="sm" onClick={() => markDone(t.id)} data-testid={`cal-mark-done-${t.id}`}>✓</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <div className="text-[11px] text-gray-500 italic">
        💡 Tip: Communication plans auto-generate up to 60 days ahead. Click a day to see all scheduled recurring tasks &amp; ad-hoc commitments.
        Numbers in red = pending tasks for that day.
      </div>
    </div>
  );
}

// ───────────────────────── My Today ─────────────────────────
function MyTodayTab({ clientLabel }) {
  const [data, setData] = useState({ tasks: [], queries: [], date: '' });
  const load = useCallback(() => {
    axios.get(`${API}/api/csrm/tasks/today`, opts).then((r) => setData(r.data || { tasks: [], queries: [] }))
      .catch((e) => toast.error('Load failed: ' + (e.response?.data?.detail || e.message)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const markDone = async (id) => {
    try {
      await axios.put(`${API}/api/csrm/tasks/${id}`, { status: 'done' }, opts);
      toast.success('Task marked done');
      load();
    } catch (e) { toast.error('Update failed: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500">Communication checklist for {data.date}</div>
      <Card className="p-4">
        <h3 className="font-semibold mb-2">Communication Tasks · {data.tasks.length}</h3>
        {data.tasks.length === 0 ? (
          <div className="text-sm text-gray-400 py-4">No tasks scheduled for today. All caught up 🎉</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Client</TableHead><TableHead>Activity</TableHead>
              <TableHead>Category</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.tasks.map((t) => (
                <TableRow key={t.id} data-testid={`today-task-${t.id}`}>
                  <TableCell className="font-medium">{clientLabel[t.client_id] || t.client_id}</TableCell>
                  <TableCell>{t.activity}</TableCell>
                  <TableCell><span className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700">{t.category}</span></TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[t.status] || ''}`}>{t.status}</span></TableCell>
                  <TableCell>
                    {t.status !== 'done' && (
                      <Button size="sm" onClick={() => markDone(t.id)} data-testid={`today-done-${t.id}`}>Mark Done</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-semibold mb-2">Open Queries · {data.queries.length}</h3>
        {data.queries.length === 0 ? (
          <div className="text-sm text-gray-400 py-4">No open queries assigned to you.</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Client</TableHead><TableHead>Source</TableHead>
              <TableHead>Description</TableHead><TableHead>Priority</TableHead>
              <TableHead>Status</TableHead><TableHead>SLA</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.queries.map((q) => (
                <TableRow key={q.id}>
                  <TableCell>{clientLabel[q.client_id] || q.client_id}</TableCell>
                  <TableCell><span className="text-xs uppercase font-bold">{q.source}</span></TableCell>
                  <TableCell className="max-w-xs truncate" title={q.description}>{q.description}</TableCell>
                  <TableCell><span className="text-xs uppercase">{q.priority}</span></TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[q.status] || ''}`}>{q.status}</span></TableCell>
                  <TableCell>{q.sla_breached ? <span className="text-rose-600 font-bold">BREACHED</span> : 'OK'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

// ───────────────────────── Plans ─────────────────────────
function PlansTab({ clients, users, clientLabel, userLabel }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: '', activity: '', category: 'SEO', frequency: 'weekly', preferred_weekday: 1, assigned_to: '', active: true });
  const load = useCallback(() => {
    axios.get(`${API}/api/csrm/plans`, opts).then((r) => setRows(r.data || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await axios.post(`${API}/api/csrm/plans`, form, opts);
      toast.success('Plan created');
      setOpen(false);
      setForm({ client_id: '', activity: '', category: 'SEO', frequency: 'weekly', preferred_weekday: 1, assigned_to: '', active: true });
      load();
    } catch (e) { toast.error('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const regen = async () => {
    try {
      const r = await axios.post(`${API}/api/csrm/tasks/generate?days_ahead=7`, {}, opts);
      toast.success(`Generated ${r.data.created} tasks for the next 7 days`);
    } catch (e) { toast.error('Generate failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    await axios.delete(`${API}/api/csrm/plans/${id}`, opts);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button onClick={() => setOpen(true)} data-testid="new-plan-btn">+ New Plan</Button>
        <Button variant="outline" onClick={regen} data-testid="regen-tasks-btn">Regenerate Tasks (7d)</Button>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Activity</TableHead>
            <TableHead>Category</TableHead><TableHead>Frequency</TableHead>
            <TableHead>Assigned</TableHead><TableHead>Active</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">No plans yet — create one to auto-generate daily comm tasks</TableCell></TableRow>
            ) : rows.map((p) => (
              <TableRow key={p.id} data-testid={`plan-row-${p.id}`}>
                <TableCell>{clientLabel[p.client_id]}</TableCell>
                <TableCell className="font-medium">{p.activity}</TableCell>
                <TableCell><span className="text-xs px-2 py-1 rounded bg-indigo-50 text-indigo-700">{p.category}</span></TableCell>
                <TableCell>{p.frequency}</TableCell>
                <TableCell>{userLabel[p.assigned_to] || '—'}</TableCell>
                <TableCell>{p.active ? '✓' : '✗'}</TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => del(p.id)}>Delete</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-plan-dialog">
          <DialogHeader><DialogTitle>New Communication Plan</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="plan-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                <SelectContent>{clients.filter((c) => !!c.id).map((c) => <SelectItem key={c.id} value={c.id}>{clientName(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Activity *</Label>
              <Input value={form.activity} onChange={(e) => setForm({ ...form, activity: e.target.value })} placeholder="e.g. Keyword Rankings Update" data-testid="plan-activity" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['SEO', 'GBP', 'GA', 'Ads', 'Social', 'general'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['daily', 'every_2_days', 'weekly', 'biweekly', 'monthly', 'quarterly'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Preferred Weekday (0=Mon)</Label>
                <Input type="number" min="0" max="6" value={form.preferred_weekday}
                  onChange={(e) => setForm({ ...form, preferred_weekday: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Assigned To</Label>
                <Select value={form.assigned_to || 'unassigned'} onValueChange={(v) => setForm({ ...form, assigned_to: v === 'unassigned' ? '' : v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">— Unassigned —</SelectItem>
                    {users.filter((u) => !!u.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!form.client_id || !form.activity} data-testid="save-plan-btn">Save Plan</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────────── Queries ─────────────────────────
function QueriesTab({ clients, users, clientLabel, userLabel }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ client_id: '', source: 'whatsapp', subject: '', description: '', priority: 'medium', assigned_to: '' });
  const load = useCallback(() => {
    const q = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
    axios.get(`${API}/api/csrm/queries${q}`, opts).then((r) => setRows(r.data || []));
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await axios.post(`${API}/api/csrm/queries`, form, opts);
      toast.success('Query logged');
      setOpen(false);
      setForm({ client_id: '', source: 'whatsapp', subject: '', description: '', priority: 'medium', assigned_to: '' });
      load();
    } catch (e) { toast.error('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/api/csrm/queries/${id}`, { status }, opts);
      toast.success('Status updated');
      load();
    } catch (e) { toast.error('Update failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this query? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/csrm/queries/${id}`, opts);
      toast.success('Query deleted');
      load();
    } catch (e) { toast.error('Delete failed: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <Button onClick={() => setOpen(true)} data-testid="new-query-btn">+ New Query</Button>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            {['all', 'open', 'in_progress', 'resolved', 'escalated'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Received</TableHead><TableHead>Client</TableHead>
            <TableHead>Source</TableHead><TableHead>Description</TableHead>
            <TableHead>Priority</TableHead><TableHead>Assigned</TableHead>
            <TableHead>Status</TableHead><TableHead>SLA</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-400">No queries. Log the next one clients send you here.</TableCell></TableRow>
            ) : rows.map((q) => (
              <TableRow key={q.id} data-testid={`query-row-${q.id}`}>
                <TableCell className="text-xs">{(q.received_at || '').slice(0, 16).replace('T', ' ')}</TableCell>
                <TableCell>{clientLabel[q.client_id] || q.client_id}</TableCell>
                <TableCell><span className="text-xs uppercase font-bold">{q.source}</span></TableCell>
                <TableCell className="max-w-xs truncate" title={q.description}>{q.description}</TableCell>
                <TableCell><span className="text-xs uppercase">{q.priority}</span></TableCell>
                <TableCell>{userLabel[q.assigned_to] || '—'}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[q.status] || ''}`}>{q.status}</span></TableCell>
                <TableCell>{q.sla_breached ? <span className="text-rose-600 font-bold">BREACHED</span> : 'OK'}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Select value={q.status} onValueChange={(v) => updateStatus(q.id, v)}>
                      <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['open', 'in_progress', 'resolved', 'escalated'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button onClick={() => del(q.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Delete query" data-testid={`del-query-${q.id}`}>🗑</button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-query-dialog">
          <DialogHeader><DialogTitle>Log Client Query</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="query-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                <SelectContent>{clients.filter((c) => !!c.id).map((c) => <SelectItem key={c.id} value={c.id}>{clientName(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['whatsapp', 'email', 'phone', 'meeting', 'walkin'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['low', 'medium', 'high', 'urgent'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="query-subject" />
            </div>
            <div>
              <Label>Description *</Label>
              <textarea rows={3} className="w-full border rounded px-2 py-1.5 text-sm"
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="query-description" />
            </div>
            <div>
              <Label>Assigned To</Label>
              <Select value={form.assigned_to || 'unassigned'} onValueChange={(v) => setForm({ ...form, assigned_to: v === 'unassigned' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">— Unassigned —</SelectItem>
                  {users.filter((u) => !!u.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!form.client_id || !form.description} data-testid="save-query-btn">Log Query</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────────── Meetings ─────────────────────────
function MeetingsTab({ clients, clientLabel }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: '', title: '', meeting_type: 'online', meeting_date: new Date().toISOString().slice(0, 10), meeting_time: '10:00', agenda: '', discussion_summary: '', key_decisions: '' });
  const load = useCallback(() => {
    axios.get(`${API}/api/csrm/meetings`, opts).then((r) => setRows(r.data || []));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await axios.post(`${API}/api/csrm/meetings`, form, opts);
      toast.success('Meeting saved');
      setOpen(false);
      setForm({ client_id: '', title: '', meeting_type: 'online', meeting_date: new Date().toISOString().slice(0, 10), meeting_time: '10:00', agenda: '', discussion_summary: '', key_decisions: '' });
      load();
    } catch (e) { toast.error('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const submitMoM = async (id) => {
    const summary = window.prompt('Discussion summary?');
    if (!summary) return;
    await axios.put(`${API}/api/csrm/meetings/${id}`, { discussion_summary: summary, mom_status: 'submitted', mom_submitted_at: new Date().toISOString() }, opts);
    toast.success('MoM submitted');
    load();
  };
  const del = async (id) => {
    if (!window.confirm('Delete this meeting and its action items? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/csrm/meetings/${id}`, opts);
      toast.success('Meeting deleted');
      load();
    } catch (e) { toast.error('Delete failed: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)} data-testid="new-meeting-btn">+ New Meeting</Button>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Client</TableHead>
            <TableHead>Title</TableHead><TableHead>Type</TableHead>
            <TableHead>MoM</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400">No meetings recorded yet.</TableCell></TableRow>
            ) : rows.map((m) => (
              <TableRow key={m.id} data-testid={`meeting-row-${m.id}`}>
                <TableCell>{m.meeting_date} {m.meeting_time}</TableCell>
                <TableCell>{clientLabel[m.client_id]}</TableCell>
                <TableCell className="font-medium">{m.title}</TableCell>
                <TableCell>{m.meeting_type}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[m.mom_status] || 'bg-amber-100 text-amber-800'}`}>{m.mom_status}</span></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {m.mom_status !== 'submitted' && (
                      <Button size="sm" onClick={() => submitMoM(m.id)} data-testid={`submit-mom-${m.id}`}>Submit MoM</Button>
                    )}
                    <button onClick={() => del(m.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Delete meeting" data-testid={`del-meeting-${m.id}`}>🗑</button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-meeting-dialog">
          <DialogHeader><DialogTitle>Record Meeting</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="meeting-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                <SelectContent>{clients.filter((c) => !!c.id).map((c) => <SelectItem key={c.id} value={c.id}>{clientName(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="meeting-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.meeting_type} onValueChange={(v) => setForm({ ...form, meeting_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['online', 'onsite', 'call'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={form.meeting_date} onChange={(e) => setForm({ ...form, meeting_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Agenda</Label>
              <textarea rows={2} className="w-full border rounded px-2 py-1.5 text-sm" value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!form.client_id || !form.title} data-testid="save-meeting-btn">Save Meeting</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────────── Action Items ─────────────────────────
function ActionItemsTab({ clients, clientLabel, userLabel }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: '', title: '', priority: 'medium', due_date: '', assigned_to: '' });
  const load = useCallback(() => axios.get(`${API}/api/csrm/action-items`, opts).then((r) => setRows(r.data || [])), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      await axios.post(`${API}/api/csrm/action-items`, form, opts);
      toast.success('Action item created');
      setOpen(false); load();
    } catch (e) { toast.error('Save failed: ' + (e.response?.data?.detail || e.message)); }
  };
  const toggle = async (id, status) => {
    await axios.put(`${API}/api/csrm/action-items/${id}`, { status }, opts);
    load();
  };
  const del = async (id) => {
    if (!window.confirm('Delete this action item? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/csrm/action-items/${id}`, opts);
      toast.success('Action item deleted');
      load();
    } catch (e) { toast.error('Delete failed: ' + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)} data-testid="new-action-item-btn">+ New Action Item</Button>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Title</TableHead>
            <TableHead>Priority</TableHead><TableHead>Due</TableHead>
            <TableHead>Assigned</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-400">No action items.</TableCell></TableRow>
            ) : rows.map((a) => (
              <TableRow key={a.id} data-testid={`action-row-${a.id}`}>
                <TableCell>{clientLabel[a.client_id]}</TableCell>
                <TableCell className="font-medium">{a.title}</TableCell>
                <TableCell><span className="text-xs uppercase">{a.priority}</span></TableCell>
                <TableCell>{a.due_date || '—'}</TableCell>
                <TableCell>{userLabel[a.assigned_to] || '—'}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[a.status] || ''}`}>{a.status}</span></TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {a.status !== 'done' && <Button size="sm" onClick={() => toggle(a.id, 'done')}>Mark Done</Button>}
                    <button onClick={() => del(a.id)} className="text-rose-500 hover:text-rose-700 p-1" title="Delete action item" data-testid={`del-action-${a.id}`}>🗑</button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-action-item-dialog">
          <DialogHeader><DialogTitle>New Action Item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="ai-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
                <SelectContent>{clients.filter((c) => !!c.id).map((c) => <SelectItem key={c.id} value={c.id}>{clientName(c)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ai-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['low', 'medium', 'high', 'urgent'].map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={!form.client_id || !form.title} data-testid="save-action-item-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ───────────────────────── Health & Manager ─────────────────────────
function HealthTab() {
  const [health, setHealth] = useState([]);
  const [dash, setDash] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/csrm/health`, opts).then((r) => setHealth(r.data || []));
    axios.get(`${API}/api/csrm/dashboard/manager`, opts).then((r) => setDash(r.data));
  }, []);
  return (
    <div className="space-y-4">
      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Overdue Tasks" value={dash.overdue_tasks} color="rose" testId="stat-overdue-tasks" />
          <StatCard label="Pending Queries" value={dash.pending_queries} color="amber" testId="stat-pending-queries" />
          <StatCard label="SLA Breached" value={dash.sla_breached} color="rose" testId="stat-sla-breached" />
          <StatCard label="Pending MoM" value={dash.pending_mom} color="amber" testId="stat-pending-mom" />
          <StatCard label="Overdue Action Items" value={dash.overdue_action_items} color="rose" testId="stat-overdue-ai" />
        </div>
      )}

      <Card className="p-4">
        <h3 className="font-semibold mb-3">Clients Without Update &gt; 48h</h3>
        {dash?.clients_without_update_48h?.length ? (
          <ul className="space-y-1 text-sm">
            {dash.clients_without_update_48h.map((c) => (
              <li key={c.id} className="flex justify-between border-b py-1"><span>{c.name}</span><span className="text-xs text-gray-500">{c.last_update_at || 'never'}</span></li>
            ))}
          </ul>
        ) : <div className="text-sm text-gray-400 py-2">All clients contacted within 48h.</div>}
      </Card>

      <Card className="p-4">
        <h3 className="font-semibold mb-3">Client Health Scores</h3>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Client</TableHead><TableHead>Score</TableHead>
            <TableHead>Colour</TableHead><TableHead>Days Since Update</TableHead>
            <TableHead>Pending Queries</TableHead><TableHead>Overdue AI</TableHead>
            <TableHead>Pending MoM</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {health.map((h) => (
              <TableRow key={h.client_id} data-testid={`health-row-${h.client_id}`}>
                <TableCell>{h.client_name}</TableCell>
                <TableCell className="font-bold">{h.score}</TableCell>
                <TableCell><span className={`text-xs px-2 py-1 rounded ${h.colour === 'green' ? 'bg-emerald-100 text-emerald-800' : h.colour === 'yellow' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>{h.colour.toUpperCase()}</span></TableCell>
                <TableCell>{h.days_since_last_update}</TableCell>
                <TableCell>{h.pending_queries}</TableCell>
                <TableCell>{h.overdue_action_items}</TableCell>
                <TableCell>{h.pending_mom}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

const StatCard = ({ label, value, color, testId }) => (
  <Card className="p-3" data-testid={testId}>
    <div className="text-[10px] uppercase font-bold text-gray-500">{label}</div>
    <div className={`text-2xl font-black mt-1 text-${color}-700`}>{value}</div>
  </Card>
);

// ───────────────────────── AI Assist ─────────────────────────
function AiTab({ clients, clientLabel }) {
  const [notes, setNotes] = useState('');
  const [mom, setMom] = useState('');
  const [items, setItems] = useState([]);
  const [aiClient, setAiClient] = useState('');
  const [draft, setDraft] = useState('');
  const [risk, setRisk] = useState('');
  const [busy, setBusy] = useState('');

  const runMoM = async () => {
    if (!notes.trim()) { toast.error('Paste some meeting notes first'); return; }
    setBusy('mom');
    try {
      const r = await axios.post(`${API}/api/csrm/ai/summarise-mom`, { notes }, opts);
      setMom(r.data.mom || '');
      toast.success('MoM ready');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(''); }
  };
  const runExtract = async () => {
    if (!notes.trim()) { toast.error('Paste some notes first'); return; }
    setBusy('extract');
    try {
      const r = await axios.post(`${API}/api/csrm/ai/extract-action-items`, { notes }, opts);
      setItems(r.data.items || []);
      toast.success(`Extracted ${r.data.items.length} action items`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(''); }
  };
  const runDraft = async () => {
    if (!aiClient) { toast.error('Pick a client'); return; }
    setBusy('draft');
    try {
      const r = await axios.post(`${API}/api/csrm/ai/draft-update`, { client_id: aiClient, channel: 'whatsapp' }, opts);
      setDraft(r.data.draft || '');
      toast.success('Draft ready');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(''); }
  };
  const runRisk = async () => {
    setBusy('risk');
    try {
      const r = await axios.get(`${API}/api/csrm/ai/at-risk-clients`, opts);
      setRisk(r.data.summary || '');
      toast.success('At-risk digest ready');
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(''); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <Card className="p-4">
        <h3 className="font-semibold mb-2">Meeting Notes → MoM + Action Items</h3>
        <textarea rows={6} className="w-full border rounded px-2 py-1.5 text-sm"
          placeholder="Paste rough meeting notes here..."
          value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ai-notes-input" />
        <div className="flex gap-2 mt-2">
          <Button onClick={runMoM} disabled={busy === 'mom'} data-testid="ai-summarise-btn">{busy === 'mom' ? 'Summarising…' : 'Summarise MoM'}</Button>
          <Button onClick={runExtract} disabled={busy === 'extract'} variant="outline" data-testid="ai-extract-btn">{busy === 'extract' ? 'Extracting…' : 'Extract Action Items'}</Button>
        </div>
        {mom && <div className="mt-3 p-2 bg-slate-50 rounded text-sm whitespace-pre-wrap" data-testid="ai-mom-output">{mom}</div>}
        {items.length > 0 && (
          <ul className="mt-3 space-y-1 text-sm" data-testid="ai-items-output">
            {items.map((it, i) => (
              <li key={i} className="border-l-4 border-emerald-400 pl-2">
                <span className="font-semibold">{it.title}</span>
                {it.assigned_to_name && <> · owner: {it.assigned_to_name}</>}
                {it.due_date && <> · due: {it.due_date}</>}
                {it.priority && <> · {it.priority}</>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-semibold mb-2">Draft Update from Recent Activity</h3>
        <div className="mb-2">
          <Label>Client</Label>
          <Select value={aiClient} onValueChange={setAiClient}>
            <SelectTrigger data-testid="ai-draft-client"><SelectValue placeholder="Pick a client" /></SelectTrigger>
            <SelectContent>{clients.filter((c) => !!c.id).map((c) => <SelectItem key={c.id} value={c.id}>{clientName(c)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={runDraft} disabled={!aiClient || busy === 'draft'} data-testid="ai-draft-btn">{busy === 'draft' ? 'Drafting…' : 'Draft WhatsApp Update'}</Button>
        {draft && <div className="mt-3 p-2 bg-slate-50 rounded text-sm whitespace-pre-wrap" data-testid="ai-draft-output">{draft}</div>}
      </Card>

      <Card className="p-4 md:col-span-2">
        <h3 className="font-semibold mb-2">At-risk Clients Digest</h3>
        <Button onClick={runRisk} disabled={busy === 'risk'} data-testid="ai-risk-btn">{busy === 'risk' ? 'Analysing…' : 'Analyse at-risk clients'}</Button>
        {risk && <div className="mt-3 p-2 bg-slate-50 rounded text-sm whitespace-pre-wrap" data-testid="ai-risk-output">{risk}</div>}
      </Card>
    </div>
  );
}
